// @ts-nocheck

import {
  CoreBatchAction,
  type ID,
  ProjectActions,
  selectBlocksByNode,
  selectTabNodes,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'
import {
  commitProjectRequestPure,
  rollbackToBackupSnapshot,
  saveBackupSnapshot,
} from '@storeOld/actionCreators'
import { useDispatch, useStore } from 'react-redux'

import { DEFAULT_PROJECT_NAME } from '@/constants/constants'
import { AI_ATTEMPTS, VITE_AI_HOST } from '@/constants/env'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import {
  clearAiNotifications,
  notifyAiGeneratingFinished,
  notifyAiInProgress,
} from '@/hooks/ai/actionCreators'
import useIndexer from '@/hooks/useIndexer'
import { isValidHexColor } from '@/utils/colors'
import { parseCsv, postEventStreamV2 } from '@/utils/streamsV2'

// The sitemap is read when the prompt is sent, not on every render of the
// action bar that hosts the menu.
const useChangeColorHandler = (): ((prompt: string) => void) => {
  const dispatch = useDispatch()
  const store = useStore<AppState>()
  const indexer = useIndexer<string>()

  return async (prompt: string) => {
    const state = store.getState()
    const projectTitle = migrationProjectSelector.getProjectTitle(state)
    const colors = migrationProjectSelector.getProjectColors(state)
    const tabId = migrationProjectSelector.getActiveOrFirstTabId(state)
    const pages = selectTabNodes(tabId)({
      project: state.manager.project,
      relations: state.relations,
    }).map(({ id, colorId, title }) => ({
      index: indexer.addAndGetIndex(id),
      name: title,
      color:
        migrationProjectSelector.getProjectColorById(colorId)(state).hexCode,
      blocks: selectBlocksByNode(id)(state)
        .map((blockId) => state.manager.project.blocks[blockId])
        .map(({ id, colorId, title }) => ({
          index: indexer.addAndGetIndex(id),
          name: title,
          color:
            migrationProjectSelector.getProjectColorById(colorId)(state)
              .hexCode,
        })),
    }))

    sendGtmEvent(GtmTriggerName.AI_PROJECT_COLORS)

    const projectName =
      projectTitle === DEFAULT_PROJECT_NAME || !projectTitle
        ? undefined
        : projectTitle

    const [stream, abort] = postEventStreamV2(
      `${VITE_AI_HOST}/v2/change-colors`,
      {
        projectName,
        prompt,
        pages,
      },
      { attempts: AI_ATTEMPTS }
    )

    dispatch(artBoardActions.setAiGeneratingStatus({ status: true }))
    dispatch(saveBackupSnapshot())
    dispatch(
      notifyAiInProgress(() => {
        abort()
        dispatch(rollbackToBackupSnapshot())
        dispatch(clearAiNotifications())
        dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      }, 'Cancel')
    )

    // @ts-expect-error
    const actionsBatch = []
    const colorsMap = new Map<string, ID>()
    // @ts-expect-error
    parseCsv(stream).on('row', ({ value }) => {
      const [type, id_index, color] = value
      if (!isValidHexColor(color)) {
        throw new Error('Invalid color')
      }
      const id = indexer.getByIndex(Number(id_index))

      if (id !== null) {
        if (!colorsMap.has(color)) {
          const colorId = getId()
          colorsMap.set(color, colorId)
          const addColorAction = ProjectActions.colors.add({
            items: [
              {
                hexCode: color,
                id: colorId,
                showInLegend: true,
                title: color,
              },
            ],
          })
          dispatch(addColorAction)
          actionsBatch.push(addColorAction)
        }
        const colorId = colorsMap.get(color)!
        const updateAction =
          type === 'p'
            ? ProjectActions.nodes.updateColor({ id, colorId })
            : ProjectActions.blocks.updateColor({
                ids: [id],
                colorId,
              })

        dispatch(updateAction)
        actionsBatch.push(updateAction)
      }
    })

    stream.on('error', () => {
      dispatch(rollbackToBackupSnapshot())
    })

    stream.on('attemptsWasted', () => {
      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
    })

    stream.on('success', () => {
      const defaultColor = colorsMap.values().next().value
      const clearColorsAction = ProjectActions.colors.delete({
        ids: colors.map((c) => c.id),
        nextColor: defaultColor,
      })
      const setDefaultColorAction = ProjectActions.settings.updateDefaultColor({
        defaultColor,
      })
      // todo: get all changes from commit action, fix settings and colors update
      dispatch(clearColorsAction)
      dispatch(setDefaultColorAction)

      actionsBatch.push(clearColorsAction)
      actionsBatch.push(setDefaultColorAction)

      dispatch(
        commitProjectRequestPure(
          CoreBatchAction({
            type: 'ai_generate_change_colors',
            actions: actionsBatch,
          })
        )
      )

      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(notifyAiGeneratingFinished())
      setTimeout(() => {
        dispatch(clearAiNotifications())
      }, 8000)
    })
  }
}

export default useChangeColorHandler
